import React from 'react'
import { API_ROOT } from '../config'

// Proxy routes on our own backend — avoids CORS and keeps the Freepik key server-side
const PROXY_BASE = `${API_ROOT}freepik`
const POLL_INTERVAL_MS = 3000

function getAuthHeaders() {
  const token = localStorage.getItem('token') || ''
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

// Compress and resize an image file using canvas before base64 encoding.
// Keeps payload small enough to pass through nginx (typically 10MB limit).
// maxDim: max width or height in pixels. quality: JPEG quality 0-1.
function compressToBase64(file, maxDim = 1536, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height / width) * maxDim)
          width = maxDim
        } else {
          width = Math.round((width / height) * maxDim)
          height = maxDim
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      // Use JPEG for compression (even if source was PNG) — Freepik accepts it
      const dataUrl = canvas.toDataURL('image/jpeg', quality)
      resolve(dataUrl.split(',')[1])
    }
    img.onerror = reject
    img.src = url
  })
}

class FreepikGeneratorView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      lockerFile: null,
      lockerPreview: null,
      bgFile: null,
      bgPreview: null,
      prompt: 'Regenerate as a photorealistic scene where the locker cabinet is physically placed inside the room. Add a realistic floor shadow below the locker that matches the room lighting direction. Match the locker lighting and color tone to the room ambient environment. Remove compositing artifacts. Keep the room walls and floor unchanged.',
      // Composite positioning controls (locker placement on background)
      compX: 50,
      compY: 60,
      compScale: 65,
      compOpacity: 100,
      // Integrate params
      aspectRatio: 'widescreen_16_9',
      // Generation state
      generating: false,
      taskId: null,
      status: null,
      resultUrl: null,
      error: null,
      pollTimer: null,
      countdownTimer: null,
      pollCountdown: 0,
    }

    this.lockerInputRef = React.createRef()
    this.bgInputRef = React.createRef()
  }

  componentWillUnmount() {
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer)
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
  }

  isRasterImage(file) {
    const supported = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!supported.includes(file.type)) {
      this.setState({ error: `Formato "${file.type || file.name}" no soportado. Usa PNG, JPG o WEBP. SVG no es compatible con la API.` })
      return false
    }
    return true
  }

  handleLockerDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && this.isRasterImage(file)) this.setLockerFile(file)
  }

  handleBgDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && this.isRasterImage(file)) this.setBgFile(file)
  }

  setLockerFile(file) {
    const url = URL.createObjectURL(file)
    this.setState({ lockerFile: file, lockerPreview: url, error: null })
  }

  setBgFile(file) {
    const url = URL.createObjectURL(file)
    this.setState({ bgFile: file, bgPreview: url, error: null })
  }

  clearResult() {
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer)
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
    this.setState({ generating: false, taskId: null, status: null, resultUrl: null, error: null, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
  }

  async generate() {
    const { lockerFile, bgFile, prompt, compX, compY, compScale, compOpacity, aspectRatio } = this.state
    if (!lockerFile || !bgFile) {
      return this.setState({ error: 'Por favor sube las dos imágenes antes de generar.' })
    }

    this.clearResult()
    this.setState({ generating: true, status: 'COMPRESSING', error: null })

    try {
      // Load both images from object URLs
      const bgUrl = URL.createObjectURL(bgFile)
      const lockerUrl = URL.createObjectURL(lockerFile)
      const [bgImg, lockerImg] = await Promise.all([
        new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = bgUrl }),
        new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = lockerUrl }),
      ])
      URL.revokeObjectURL(bgUrl)
      URL.revokeObjectURL(lockerUrl)

      // Canvas composite — resize bg for upload, overlay locker PNG (transparent) at user position
      const maxDim = 1536
      let bgW = bgImg.naturalWidth, bgH = bgImg.naturalHeight
      if (bgW > maxDim || bgH > maxDim) {
        if (bgW >= bgH) { bgH = Math.round(bgH / bgW * maxDim); bgW = maxDim }
        else { bgW = Math.round(bgW / bgH * maxDim); bgH = maxDim }
      }
      const canvas = document.createElement('canvas')
      canvas.width = bgW
      canvas.height = bgH
      const ctx = canvas.getContext('2d')
      ctx.drawImage(bgImg, 0, 0, bgW, bgH)
      // Scale locker by HEIGHT of background — lockers are portrait objects
      const lockerH = bgH * (compScale / 100)
      const lockerW = (lockerImg.naturalWidth / lockerImg.naturalHeight) * lockerH
      const lx = (bgW * (compX / 100)) - lockerW / 2
      const ly = (bgH * (compY / 100)) - lockerH / 2
      ctx.globalAlpha = compOpacity / 100
      ctx.drawImage(lockerImg, lx, ly, lockerW, lockerH)
      ctx.globalAlpha = 1

      const compositeB64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1]

      // Compress locker + bg to ~1024px before sending — keeps total payload well under 10MB
      const [lockerB64, bgB64] = await Promise.all([
        compressToBase64(lockerFile, 1024, 0.85),
        compressToBase64(bgFile, 1024, 0.85),
      ])

      const res = await fetch(`${PROXY_BASE}/integrate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          composite: compositeB64,
          lockerImage: lockerB64,
          bgImage: bgB64,
          prompt,
          aspectRatio,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || err.error || `Error ${res.status}`)
      }

      const data = await res.json()
      const taskId = data.data?.task_id
      if (!taskId) throw new Error('No se recibió task_id de la API.')

      this.setState({ taskId, status: data.data?.status || 'CREATED' })
      this.schedulePoll(taskId)

    } catch (e) {
      this.setState({ generating: false, error: e.message || 'Error desconocido.' })
    }
  }

  schedulePoll(taskId) {
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
    const secs = POLL_INTERVAL_MS / 1000
    this.setState({ pollCountdown: secs })
    const countdownTimer = setInterval(() => {
      this.setState(prev => {
        if (prev.pollCountdown <= 1) {
          clearInterval(prev.countdownTimer)
          return { pollCountdown: 0, countdownTimer: null }
        }
        return { pollCountdown: prev.pollCountdown - 1 }
      })
    }, 1000)
    const timer = setTimeout(() => this.poll(taskId), POLL_INTERVAL_MS)
    this.setState({ pollTimer: timer, countdownTimer })
  }

  async poll(taskId) {
    try {
      const res = await fetch(`${PROXY_BASE}/integrate/${taskId}`, {
        headers: getAuthHeaders(),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || `Error ${res.status}`)
      }

      const data = await res.json()
      const { status } = data.data || {}

      this.setState({ status })

      if (status === 'COMPLETED') {
        // Flux Kontext Pro — try multiple common response field patterns
        const d = data.data || {}
        const url = (d.generated && d.generated[0]) || d.result || d.url || (d.output && d.output[0])
        if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
        this.setState({ generating: false, resultUrl: url || null, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
      } else if (status === 'FAILED' || status === 'ERROR') {
        const d = data.data || {}
        const msg = d.error || d.message || d.reason || JSON.stringify(d)
        throw new Error(`Magnific: ${msg}`)
      } else {
        // Still in progress — keep polling
        this.schedulePoll(taskId)
      }
    } catch (e) {
      if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
      this.setState({ generating: false, error: e.message, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
    }
  }

  async downloadComposite() {
    const { lockerPreview, bgPreview, compX, compY, compScale, compOpacity } = this.state
    if (!lockerPreview || !bgPreview) return
    const [bgImg, lockerImg] = await Promise.all([
      new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = bgPreview }),
      new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = lockerPreview }),
    ])
    const canvas = document.createElement('canvas')
    canvas.width = bgImg.naturalWidth
    canvas.height = bgImg.naturalHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(bgImg, 0, 0)
    // Scale locker by HEIGHT of background — lockers are portrait objects
    const lockerH = bgImg.naturalHeight * (compScale / 100)
    const lockerW = (lockerImg.naturalWidth / lockerImg.naturalHeight) * lockerH
    const lx = (bgImg.naturalWidth * (compX / 100)) - lockerW / 2
    const ly = (bgImg.naturalHeight * (compY / 100)) - lockerH / 2
    ctx.globalAlpha = compOpacity / 100
    ctx.drawImage(lockerImg, lx, ly, lockerW, lockerH)
    ctx.globalAlpha = 1
    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'locker-composite.png'; a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  renderPollLoader() {
    const { generating, status, pollCountdown } = this.state
    if (!generating || !status || status === 'COMPRESSING' || status === 'COMPLETED') return null
    const secs = POLL_INTERVAL_MS / 1000
    const pct = pollCountdown === 0 ? 100 : Math.round(((secs - pollCountdown) / secs) * 100)
    const isWaiting = pollCountdown > 0
    return (
      <div style={{ marginTop: 14 }}>
        <div style={
          { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 6, fontSize: 12, color: '#6b7280' }
        }>
          <span>
            {isWaiting
              ? <><i className="fas fa-hourglass-half mr-1" style={{ color: '#8b5cf6' }}></i>Próxima consulta en <strong>{pollCountdown}s</strong></>  
              : <><i className="fas fa-sync fa-spin mr-1" style={{ color: '#6366f1' }}></i>Consultando...</>}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {this.renderStatusBadge()}
          </span>
        </div>
        {/* Progress bar */}
        <div style={{
          height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: isWaiting
              ? 'linear-gradient(90deg, #6366f1, #8b5cf6)'
              : 'linear-gradient(90deg, #8b5cf6, #6366f1)',
            borderRadius: 99,
            transition: isWaiting ? 'width 1s linear' : 'width 0.2s ease',
          }} />
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
          Generación AI puede tardar 30–90 segundos
        </div>
      </div>
    )
  }

  renderDropZone(label, icon, preview, onClick, onDrop, onClear) {
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
          <i className={`fas ${icon} mr-2`}></i>{label}
        </div>
        <div
          onClick={onClick}
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          style={{
            border: preview ? '2px solid #6366f1' : '2px dashed #d1d5db',
            borderRadius: 12,
            background: preview ? '#000' : '#f9fafb',
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
          }}
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="preview"
                style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', display: 'block' }}
              />
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onClear() }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,.55)', color: '#fff', border: 'none',
                  borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}
                title="Eliminar"
              >
                <i className="fas fa-times"></i>
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: 36, marginBottom: 10, display: 'block' }}></i>
              <div style={{ fontSize: 13 }}>Arrastra aquí o haz clic para seleccionar</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>PNG, JPG, WEBP — SVG no soportado</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  renderStatusBadge() {
    const { status } = this.state
    if (!status) return null
    const map = {
      COMPRESSING: ['#6366f1', 'Comprimiendo...'],
      CREATED: ['#6366f1', 'Creado'],
      IN_PROGRESS: ['#f59e0b', 'Generando...'],
      COMPLETED: ['#10b981', 'Completado'],
      FAILED: ['#ef4444', 'Fallido'],
      ERROR: ['#ef4444', 'Error'],
    }
    const [color, label] = map[status] || ['#9ca3af', status]
    return (
      <span style={{
        background: color, color: '#fff', padding: '3px 12px', borderRadius: 20,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
      }}>
        {label}
      </span>
    )
  }

  render() {
    const {
      lockerFile, lockerPreview, bgFile, bgPreview,
      prompt, compX, compY, compScale, compOpacity, aspectRatio,
      generating, status, resultUrl, error,
    } = this.state

    const canGenerate = !!lockerFile && !!bgFile && !generating

    return (
      <div style={{ maxWidth: '100%', padding: '24px 20px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1f2937', margin: 0 }}>
            <i className="fas fa-magic mr-3" style={{ color: '#6366f1' }}></i>
            Generador de Imágenes AI
          </h1>
          <p style={{ color: '#6b7280', marginTop: 6, marginBottom: 0 }}>
            Posiciona el locker en el fondo y genera la integración realista con Seedream 4.5 Edit (Magnific AI).
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, alignItems: 'start' }}>

          {/* ── Col 1: Image uploads ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className="fas fa-images mr-2" style={{ color: '#6366f1' }}></i>Imágenes de entrada
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {this.renderDropZone(
                'Imagen 1 – Configuración de Lockers',
                'fa-cube',
                lockerPreview,
                () => this.lockerInputRef.current.click(),
                e => this.handleLockerDrop(e),
                () => this.setState({ lockerFile: null, lockerPreview: null }),
              )}
              <input
                ref={this.lockerInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && this.isRasterImage(e.target.files[0]) && this.setLockerFile(e.target.files[0])}
              />

              {this.renderDropZone(
                'Imagen 2 – Espacio / Fondo',
                'fa-image',
                bgPreview,
                () => this.bgInputRef.current.click(),
                e => this.handleBgDrop(e),
                () => this.setState({ bgFile: null, bgPreview: null }),
              )}
              <input
                ref={this.bgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && this.isRasterImage(e.target.files[0]) && this.setBgFile(e.target.files[0])}
              />
            </div>
          </div>

          {/* ── Col 2: Config + Generate ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className="fas fa-sliders-h mr-2" style={{ color: '#6366f1' }}></i>Configuración de generación
            </div>
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
              padding: '16px', marginBottom: 16,
            }}>

              {/* Prompt */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  Prompt (descripción)
                </label>
                <textarea
                  value={prompt}
                  onChange={e => this.setState({ prompt: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%', borderRadius: 8, border: '1px solid #d1d5db',
                    padding: '8px 10px', fontSize: 12, resize: 'vertical',
                    fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Aspect ratio */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  Relación de aspecto
                </label>
                <select
                  value={aspectRatio}
                  onChange={e => this.setState({ aspectRatio: e.target.value })}
                  style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: '6px 8px', fontSize: 12 }}
                >
                  <option value="widescreen_16_9">16:9 Panorámica</option>
                  <option value="classic_4_3">4:3 Clásica</option>
                  <option value="square_1_1">1:1 Cuadrada</option>
                  <option value="standard_3_2">3:2 Estándar</option>
                  <option value="traditional_3_4">3:4 Vertical</option>
                  <option value="portrait_2_3">2:3 Retrato</option>
                </select>
              </div>

              {/* Sliders */}
              {[
                { label: 'Posición horizontal', key: 'compX', value: compX, min: 0, max: 100, step: 1, hint: 'izq → der' },
                { label: 'Posición vertical', key: 'compY', value: compY, min: 0, max: 100, step: 1, hint: 'arriba → abajo' },
                { label: 'Tamaño del locker', key: 'compScale', value: compScale, min: 3, max: 60, step: 1, hint: '% del alto del fondo' },
              ].map(({ label, key, value, min, max, step, hint }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span>{label} <span style={{ fontWeight: 400, color: '#9ca3af' }}>({hint})</span></span>
                    <strong style={{ color: '#374151' }}>{value}</strong>
                  </label>
                  <input
                    type="range" min={min} max={max} step={step}
                    value={value}
                    onChange={e => this.setState({ [key]: Number(e.target.value) })}
                    style={{ width: '100%' }}
                  />
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, color: '#b91c1c', fontSize: 13,
              }}>
                <i className="fas fa-exclamation-triangle mr-2"></i>{error}
              </div>
            )}

            {/* Download composite — free, instant */}
            <button
              type="button"
              onClick={() => this.downloadComposite()}
              disabled={!lockerPreview || !bgPreview}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 10, marginBottom: 8,
                border: '2px solid #10b981', background: 'transparent',
                color: lockerPreview && bgPreview ? '#059669' : '#9ca3af',
                fontWeight: 700, fontSize: 13,
                cursor: lockerPreview && bgPreview ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <i className="fas fa-download"></i>Descargar composite (gratis)
            </button>

            {/* Generate button — Flux Kontext Pro */}
            <button
              type="button"
              onClick={() => this.generate()}
              disabled={!canGenerate}
              style={{
                width: '100%',
                padding: '13px 0',
                borderRadius: 10,
                border: 'none',
                background: canGenerate
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : '#e5e7eb',
                color: canGenerate ? '#fff' : '#9ca3af',
                fontWeight: 700,
                fontSize: 15,
                cursor: canGenerate ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {generating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Refinando... {this.renderStatusBadge()}
                </>
              ) : (
                <>
                  <i className="fas fa-magic"></i>
                  Integrar con AI · ~€0.05
                </>
              )}
            </button>

            {/* Polling loader */}
            {this.renderPollLoader()}
          </div>

          {/* ── Col 3: Live preview / AI Result ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className={`fas ${resultUrl ? 'fa-star' : 'fa-eye'} mr-2`} style={{ color: '#6366f1' }}></i>
              {resultUrl ? 'Resultado Integración AI' : 'Vista previa del composite'}
            </div>
            <div style={{
              border: `1px solid ${resultUrl ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 16,
              overflow: 'hidden', background: '#111',
            }}>
              {resultUrl ? (
                <>
                  <div style={{
                    padding: '10px 14px', background: '#1a1a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#d1d5db' }}>Seedream 4.5 Edit</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => this.setState({ resultUrl: null })}
                        style={{ background: 'transparent', border: '1px solid #4b5563', color: '#9ca3af', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>
                        ← composite
                      </button>
                      <a href={resultUrl} download="integrated-result.jpg" target="_blank" rel="noopener noreferrer"
                        style={{ background: '#10b981', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                        <i className="fas fa-download mr-1"></i>Descargar
                      </a>
                    </div>
                  </div>
                  <img src={resultUrl} alt="Resultado AI" style={{ width: '100%', display: 'block' }} />
                </>
              ) : generating ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
                  <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                    <i className="fas fa-magic fa-spin" style={{ fontSize: 40, color: '#8b5cf6', display: 'block', marginBottom: 12 }}></i>
                    <div style={{ fontWeight: 600, color: '#6b7280' }}>Integrando imágenes con AI...</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>Puede tardar 30–90 segundos</div>
                  </div>
                </div>
              ) : bgPreview ? (
                <div style={{ position: 'relative', width: '100%' }}>
                  <img src={bgPreview} alt="fondo" style={{ width: '100%', display: 'block' }} />
                  {lockerPreview && (
                    <img
                      src={lockerPreview}
                      alt="locker"
                      style={{
                        position: 'absolute',
                        left: `${compX}%`,
                        top: `${compY}%`,
                        height: `${compScale}%`,
                        width: 'auto',
                        transform: 'translate(-50%, -50%)',
                        opacity: compOpacity / 100,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
                  <div style={{ textAlign: 'center', color: '#4b5563', padding: 32 }}>
                    <i className="fas fa-image" style={{ fontSize: 48, display: 'block', marginBottom: 12 }}></i>
                    <div style={{ fontSize: 13 }}>Sube el fondo para ver la vista previa</div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    )
  }
}

export default FreepikGeneratorView
